<template>
  <div
    class="fts-html-viewer"
    :data-html-viewer-load="loading ? 'loading' : 'loaded'"
  >
    <fts-spinner full-screen boundary="parent" v-if="loading" />

    <div class="error-wrapper" v-else-if="error">
      <div class="error-icon">
        <font-awesome-icon :icon="['fas', 'times']" fixed-width />
      </div>
      <div class="error-title">Something went wrong</div>
      <div class="error-description">{{error}}</div>
    </div>

    <template v-else>
      <div class="html-viewer-header" ref="html-viewer-header">
        <fts-label-group :item="module" emoji class="html-viewer-header-name" />
        
        <div class="html-viewer-header-action">
          <fts-btn
            color="primary"
            outline
            text-transform-init
            box-shadow-none
            :loading="requesting"
            @click="exportRequest"
          >
            <font-awesome-icon :icon="['far', 'envelope']" /> Request Data
          </fts-btn>

          <fts-btn
            color="var(--fts-file-excel)"
            outline
            text-transform-init
            box-shadow-none
            :loading="exporting"
            @click="exportExcel"
          >
            <font-awesome-icon :icon="['fas', 'file-download']" /> Export to Excel
          </fts-btn>
        </div>
      </div>
      <div class="html-viewer-iframe" ref="htmlViewerIframe" />

      <fts-feedback-indicate modal ref="ftsFeedbackIndicate" />
    </template>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';
import webAppConfig from '../commons/webApp.config';
import { sendWithTokenRefreshCallback } from '../commons/documentRequest';

import FTSBtn from '../elements/FTSBtn';
import FTSFeedbackIndicate from '../elements/FTSFeedbackIndicate';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSSpinner from '../elements/FTSSpinner';

var mime = require("mime-types");

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-label-group": FTSLabelGroup,
    "fts-spinner": FTSSpinner
  },
  data: () => ({
    loading: false,
    exporting: false,
    requesting: false,
    moduleId: null,
    error: null
  }),
  methods: {
    async exportRequest() {
      try {
        this.requesting = true;
        let queryModel = JSON.parse(this.urlSource);
        await clientStore.dispatch("exportRequest", {
          moduleId: this.moduleId,
          query: queryModel,
        });

        this.$refs.ftsFeedbackIndicate.show({
          type: "success",
          desc: "Request submitted. Report will be sent midnight."
        });
      } catch(err) {
        this.$toasted.global.error(err, "Failed to request data. Please try again later.");
      } finally {
        this.requesting = false;
      }
    },
    onExportExcelError(message) {
      console.error(message);

      this.$toasted.global.error({
        message: "Failed to export data to Excel. Please try again later."
      });
      this.exporting = false;
    },
    exportExcel() {
      let vm = this;
      this.exporting = true;

      let queryModel = JSON.parse(this.urlSource);
      queryModel["outputType"] = 4;
      const serviceAPI = "/service/pdftemplateprocessing/document";
      /* SS05651: an expired access token is renewed and the export asked for again, as the shared
         HTTP client does for every other request; each attempt reads the token when it is sent. */
      const sendExport = (answer) => {
        let xhr = new XMLHttpRequest();
        let token = JSON.parse(localStorage.getItem("ls.authorizationData"))["access_token"];
        xhr.open(
          "POST",
          webAppConfig.baseURL ? webAppConfig.baseURL + serviceAPI : serviceAPI,
          true
        );
        xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhr.setRequestHeader("authorization", "bearer " + token);
        xhr.responseType = "arraybuffer";
        xhr.onload = () => answer(xhr);
        // network errors (e.g., no connection)
        xhr.onerror = function () {
          vm.onExportExcelError("Network Error or CORS issue");
        };
        xhr.send(JSON.stringify(queryModel));
      };
      sendWithTokenRefreshCallback(sendExport, () => authorizationStore.dispatch("refreshToken"), (xhr) => {
        if (xhr.status === 200) {
          var filename = "";
          var disposition = xhr.getResponseHeader("Content-Disposition");
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1])
              filename = matches[1].replace(/['"]/g, "");
          }
          var type = xhr.getResponseHeader("Content-Type");
          var blob;
          if (typeof File === "function") {
            try {
              blob = new File([xhr.response], filename, { type: type });
            } catch (err) {
              console.warn(err);
            }
          }
          if (typeof blob === "undefined") {
            blob = new Blob([xhr.response], { type: type });
          }
          if (typeof window.navigator.msSaveBlob !== "undefined") {
            // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed."
            window.navigator.msSaveBlob(blob, filename);
          } else {
            var URL = window.URL || window.webkitURL;
            var downloadUrl = URL.createObjectURL(blob);
            /* export file name */
            let exportFileName = filename;
            if (!exportFileName) {
              exportFileName = "Inistate-Export";
              if (vm.module.name) exportFileName += `-${vm.module.name}`;
              /* export file extension */
              const fileExtension = mime.extension(blob && blob.type);
              if (fileExtension) exportFileName += `.${fileExtension}`;
            }

            if (exportFileName) {
              // use HTML5 a[download] attribute to specify filename
              var a = document.createElement("a");
              // safari doesn't support this yet
              if (typeof a.download === "undefined") {
                window.location = downloadUrl;
              } else {
                a.href = downloadUrl;
                a.download = exportFileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }
            } else {
              window.location = downloadUrl;
            }
            setTimeout(function () {
              URL.revokeObjectURL(downloadUrl);
            }, 100); // cleanup
          }

          vm.exporting = false;
        } else {
          vm.onExportExcelError(`HTTP Error Code: ${xhr.status}, Error: ${xhr.statusText}`);
        }
      });
    },
  },
  computed: {
    urlSource() {
      return (
        this.$route.query &&
        this.$route.query.file &&
        this.$route.query.file.substring(55)
      );
    },
    module() {
      const findModule = clientStore.state.workspaceModules.find(
        (x) => x.id == this.moduleId
      );
      return findModule
        ? { emoji: findModule.emoji, name: findModule.name }
        : {};
    }
  },
  async mounted() {
    let responseData = null;
    let errorMessage = null;
    this.loading = true;

    if (this.urlSource) {
      // load module:prepare for the filename
      try {
        const source = JSON.parse(this.urlSource);
        if (source.parameters.vectorId) {
          this.moduleId = source.parameters.vectorId;
          if (!this.module.name) {
            await clientStore.dispatch("loadWorkspaceModule", {
              moduleId: this.moduleId,
              listId: source.parameters.list,
            });
          }
        }
      } catch (error) {
        console.error("Failed to load module", error);
      }

      try {
        const res = await clientStore.dispatch(
          "htmlTemplateProcess",
          this.urlSource
        );

        if (res.data) {
          responseData = res.data;
        } else {
          // error:without response data
          errorMessage = "Failed to retrieve export data. Please try again.";
        }
      } catch(error) {
        console.error("Failed to load html template", error);

        // SS06052: a 400 carries the actionable text in `message`; a 500 wraps it as the generic
        // "An error has occurred." and puts the real one in `exceptionMessage` (server-side that
        // detail is only returned to admins/internal users). Prefer whichever actually says
        // something, so the viewer stops showing "An error has occurred." on its own.
        const data = (error.response && error.response.data) || null;

        if (data && (data.exceptionMessage || data.message)) {
          errorMessage = data.exceptionMessage || data.message;
        } else {
          errorMessage = "Failed to retrieve export data. Please try again.";
        }
      }
    } else {
      // error:url source incorrect
      errorMessage = "The URL is incorrect. Please check and try again.";
    }

    if (errorMessage) {
      this.error = errorMessage;
    }

    this.loading = false;
    
    if (responseData) {
      this.$nextTick(() => {
        let iframe = this.$refs.htmlViewerIframe;
        iframe.innerHTML = responseData;
      });
    }
  }
};
</script>

<style lang="scss">
.fts-html-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .html-viewer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--fts-border);
  }
  .html-viewer-header-name {
    .label-group__label {
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }
  .html-viewer-header-action {
    display: flex;
    align-items: center;
    margin-left: 1rem;
    > *:not(:first-child) {
      margin-left: 0.5rem;
    }
  }
  .html-viewer-iframe {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
    overflow: auto;
    color: var(--fts-text);
  }

  .error-wrapper {
    padding: 3rem 2rem;
    text-align: center;
    .error-icon {
      height: 80px;
      width: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.25rem;
      border: 2px solid var(--fts-danger);
      border-radius: 50%;
      color: var(--fts-danger);
      > svg {
        color: inherit;
        font-size: 2.5rem;
      }
    }
    .error-title {
      margin-bottom: 0.75rem;
      color: var(--fts-danger);
      font-weight: 500;
      font-size: 1.25rem;
      line-height: 1.75rem;
      word-break: break-word;
    }
    .error-description {
      color: var(--fts-default);
      font-size: 0.875rem;
      line-height: 1.25rem;
      word-break: break-word;
    }
  }
}
</style>